import React from "react";
import ReactDOM from "react-dom";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "./index.scss";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import { notifyUpdateAvailable } from "./utils/appUpdate";
import "./styles/global.scss";
import "./aws-config";
import "./i18n";
import { AuthProvider } from "./context/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import { initRum } from "./utils/analytics";
import { initSentry } from "./utils/sentry";

// Initialized here, before the service-worker "settle" check below (which
// can delay mounting by up to SETTLE_TIMEOUT_MS for a returning visitor) -
// CloudWatch RUM measures real page-load timing, so it must start observing
// as early as possible rather than being gated behind that delay. A no-op
// when REACT_APP_RUM_* env vars aren't configured (see
// utils/analytics/providers/rum.ts).
initRum();
initSentry();

function mountApp() {
  ReactDOM.render(
    <React.StrictMode>
      <ErrorBoundary>
        <HelmetProvider>
          <BrowserRouter>
            <AuthProvider>
              <App />
            </AuthProvider>
          </BrowserRouter>
        </HelmetProvider>
      </ErrorBoundary>
    </React.StrictMode>,
    document.getElementById("root")
  );

  reportWebVitals();
}

// A deployed update otherwise sits in the service worker's "waiting" state
// until every open tab for the site is closed, so returning visitors keep
// running stale JS indefinitely - the onUpdate callback below deals with
// that, but *how* depends on whether anything has painted yet.
//
// Before the app has mounted, forcing the swap is invisible - there's
// nothing on screen yet to disrupt, and skipping straight to the reload
// avoids the alternative of painting stale content for a moment only to
// yank it away once the browser notices the update. So a *returning*
// visitor (one who already has a service worker controlling this browser
// from an earlier visit, and so is the only one who could be running
// something stale) is held behind the static loading spinner in index.html
// for a short grace window: if an update is found within it (the common
// case - a tab freshly opened right after a deploy), mounting is skipped
// entirely in favor of going straight to the reload; the app only mounts
// once nothing turns up in time. A brand-new visitor has no prior service
// worker to be stale, so they mount instantly with no added delay.
//
// Once the app HAS mounted, though, a silent forced reload is exactly the
// disruption above was trying to avoid - it yanks an already-in-use tab out
// from under whatever the user was doing (mid-quiz, mid-typing) with zero
// warning. An update found after mounting instead notifies
// UpdateAvailableBanner (see src/utils/appUpdate.ts) so the user can choose
// when to refresh, rather than having it forced on them.
const SETTLE_TIMEOUT_MS = 1500;

async function registerServiceWorkerAndBootstrap() {
  if (process.env.NODE_ENV !== "production" || !("serviceWorker" in navigator)) {
    mountApp();
    return;
  }

  let mounted = false;
  let reloadPending = false;

  function mountOnce() {
    if (mounted || reloadPending) return;
    mounted = true;
    mountApp();
  }

  function handleUpdate(registration: ServiceWorkerRegistration) {
    if (reloadPending) return;
    if (!mounted) {
      reloadPending = true;
      navigator.serviceWorker.addEventListener(
        "controllerchange",
        () => window.location.reload(),
        { once: true }
      );
      registration.waiting?.postMessage({ type: "SKIP_WAITING" });
      return;
    }
    notifyUpdateAvailable(registration);
  }

  const existingRegistration = await navigator.serviceWorker.getRegistration().catch(() => null);
  serviceWorkerRegistration.register({ onUpdate: handleUpdate });

  if (!existingRegistration) {
    mountOnce();
    return;
  }

  setTimeout(mountOnce, SETTLE_TIMEOUT_MS);
}

registerServiceWorkerAndBootstrap();
